import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type AgentType = 'patient' | 'customer' | 'examiner' | 'coach';
export type GenerationCategory = 'agent' | 'gamification';

export interface AttachmentData {
  images?: { name: string; base64: string }[];
  documents?: { name: string; text: string }[];
}

export interface GenerateOptions {
  field: string;
  agentType?: AgentType;
  context: Record<string, string>;
  customPrompt?: string;
  variations?: number;
  category?: GenerationCategory;
  attachments?: AttachmentData;
}

export interface GenerateResult {
  success: boolean;
  results: string[];
  error?: string;
}

export interface UseAIGenerateReturn {
  generate: (options: GenerateOptions) => Promise<string[]>;
  generateBatch: (fields: string[], agentType: AgentType, context: Record<string, string>) => Promise<Record<string, string>>;
  isLoading: boolean;
  error: string | null;
}

// Field labels for UI display
export const FIELD_LABELS: Record<string, string> = {
  // Common fields
  name: 'Name',
  title: 'Role / Title',
  description: 'Description',
  personality: 'Personality',
  communication_style: 'Communication Style',
  backstory: 'Backstory',
  objectives: 'Objectives',
  first_message: 'First Message',
  scenario_context: 'Scenario Context',

  // Patient fields
  presenting_complaint: 'Presenting Complaint',
  medical_history: 'Medical History',
  occupation: 'Occupation',

  // Customer fields
  company_organization: 'Company Name',
  pain_points: 'Pain Points',
  common_objections: 'Common Objections',
  buying_criteria: 'Buying Criteria',
  budget_authority: 'Budget Authority',

  // Examiner fields
  case_summary: 'Case Summary',
  key_questions: 'Key Questions',
  expected_answers: 'Expected Answers',
  followup_probes: 'Follow-up Probes',
  pass_criteria: 'Pass Criteria',
  examiner_personality: 'Examiner Personality',
  station_title: 'Station Title',

  // Coach fields
  coaching_style: 'Coaching Style',

  // Gamification fields
  badge_name: 'Badge Name',
  badge_description: 'Badge Description',
  milestone_name: 'Milestone Name',
  milestone_description: 'Milestone Description',
  reward_name: 'Reward Name',
  reward_description: 'Reward Description',
  welcome_message: 'Welcome Message',

  // Pathway fields
  pathway_name: 'Pathway Name',
  pathway_description: 'Pathway Description',
  certification_name: 'Certification Name',
  module_title: 'Module Title',
  module_description: 'Module Description',
};

// Get available fields for each agent type
export const AGENT_TYPE_FIELDS: Record<AgentType, string[]> = {
  patient: [
    'name',
    'title',
    'description',
    'personality',
    'communication_style',
    'backstory',
    'objectives',
    'first_message',
    'presenting_complaint',
    'medical_history',
    'occupation',
  ],
  customer: [
    'name',
    'title',
    'description',
    'personality',
    'communication_style',
    'backstory',
    'objectives',
    'first_message',
    'company_organization',
    'pain_points',
    'common_objections',
    'buying_criteria',
    'budget_authority',
  ],
  examiner: [
    'name',
    'title',
    'description',
    'personality',
    'communication_style',
    'backstory',
    'objectives',
    'first_message',
    'case_summary',
    'key_questions',
    'expected_answers',
    'followup_probes',
    'pass_criteria',
    'examiner_personality',
    'station_title',
  ],
  coach: [
    'name',
    'title',
    'description',
    'personality',
    'communication_style',
    'backstory',
    'objectives',
    'first_message',
    'coaching_style',
    'scenario_context',
  ],
};

// Gamification fields
export const GAMIFICATION_FIELDS = [
  'badge_name',
  'badge_description',
  'milestone_name',
  'milestone_description',
  'reward_name',
  'reward_description',
  'welcome_message',
];

export function useAIGenerate(): UseAIGenerateReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async (options: GenerateOptions): Promise<string[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-ai-content', {
        body: {
          field: options.field,
          agentType: options.agentType,
          context: options.context,
          customPrompt: options.customPrompt,
          variations: options.variations || 1,
          category: options.category || 'agent',
          attachments: options.attachments,
        },
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to generate content');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Generation failed');
      }

      return data.results || [];
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate content';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateBatch = useCallback(async (
    fields: string[],
    agentType: AgentType,
    context: Record<string, string>
  ): Promise<Record<string, string>> => {
    setIsLoading(true);
    setError(null);

    const results: Record<string, string> = {};

    try {
      // Generate fields sequentially to avoid rate limits
      // and to allow later fields to use earlier generated values as context
      for (const field of fields) {
        const generated = await generate({
          field,
          agentType,
          context: { ...context, ...results }, // Include already generated values
          variations: 1,
        });

        if (generated.length > 0) {
          results[field] = generated[0];

          // Update context for subsequent generations
          // Map field names to context keys
          const contextKey = fieldToContextKey(field);
          if (contextKey) {
            context[contextKey] = generated[0];
          }
        }
      }

      toast.success(`Generated ${Object.keys(results).length} fields successfully`);
      return results;
    } catch (err: any) {
      const errorMessage = err.message || 'Batch generation failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [generate]);

  return {
    generate,
    generateBatch,
    isLoading,
    error,
  };
}

// Helper to map field names to context keys used in prompts
function fieldToContextKey(field: string): string | null {
  const mapping: Record<string, string> = {
    name: 'name',
    title: 'title',
    company_organization: 'company',
    presenting_complaint: 'presentingComplaint',
    medical_history: 'medicalHistory',
    pain_points: 'painPoints',
    common_objections: 'commonObjections',
    buying_criteria: 'buyingCriteria',
    budget_authority: 'budgetAuthority',
    case_summary: 'caseSummary',
    key_questions: 'keyQuestions',
    examiner_personality: 'examinerPersonality',
    coaching_style: 'coachingStyle',
    personality: 'personality',
    scenario_context: 'scenario',
    station_title: 'stationTitle',
  };
  return mapping[field] || null;
}

// Helper to build context from form data
export function buildContextFromFormData(
  formData: Record<string, any>,
  agentType: AgentType,
  additionalContext?: Record<string, string>
): Record<string, string> {
  const context: Record<string, string> = {
    agentType,
    ...additionalContext,
  };

  // Map form data to context
  const mappings: Record<string, string> = {
    name: 'name',
    title: 'title',
    specialty: 'specialty',
    difficulty: 'difficulty',
    gender: 'gender',
    age: 'age',
    experience: 'experience',
    industry: 'industry',
    company_organization: 'company',
    presenting_complaint: 'presentingComplaint',
    medical_history: 'medicalHistory',
    occupation: 'occupation',
    pain_points: 'painPoints',
    common_objections: 'commonObjections',
    buying_criteria: 'buyingCriteria',
    budget_authority: 'budgetAuthority',
    case_summary: 'caseSummary',
    key_questions: 'keyQuestions',
    expected_answers: 'expectedAnswers',
    examiner_personality: 'examinerPersonality',
    examination_type: 'examinationType',
    station_title: 'stationTitle',
    coaching_style: 'coachingStyle',
    personality: 'personality',
    communication_style: 'communicationStyle',
    backstory: 'backstory',
    objectives: 'objectives',
    scenario_context: 'scenario',
  };

  for (const [formKey, contextKey] of Object.entries(mappings)) {
    const value = formData[formKey];
    if (value !== undefined && value !== null && value !== '') {
      context[contextKey] = String(value);
    }
  }

  return context;
}

// Smart suggestions based on agent type and current context
export function getSmartSuggestions(
  agentType: AgentType,
  currentField: string,
  context: Record<string, string>
): string[] {
  const suggestions: string[] = [];

  switch (agentType) {
    case 'patient':
      if (currentField === 'difficulty') {
        suggestions.push(
          'beginner - Cooperative patient, clear symptoms',
          'intermediate - Typical presentation, some complexity',
          'advanced - Challenging history, atypical presentation'
        );
      }
      break;
    case 'customer':
      if (currentField === 'difficulty') {
        suggestions.push(
          'beginner - Warm lead, clear buying intent',
          'intermediate - Standard prospect, some objections',
          'advanced - Tough buyer, multiple stakeholders'
        );
      }
      break;
    case 'examiner':
      if (currentField === 'examination_type') {
        suggestions.push('OSCE', 'Viva', 'Case Presentation', 'Clinical Reasoning', 'Mini-CEX');
      }
      break;
    case 'coach':
      if (currentField === 'coaching_style') {
        suggestions.push('challenger', 'consultative', 'socratic', 'supportive');
      }
      break;
  }

  return suggestions;
}
